import { forwardRef, ReactNode } from 'react';
import { ExclamationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';

import { EditCellVariant } from '../cellVariant';
import { SuggestedContentIcon } from '../components/SuggestedContentIcon/SuggestedContentIcon';

export type CellShellMode = 'view' | 'edit' | 'create';

type CellShellProps = {
  variant: EditCellVariant;
  mode: CellShellMode;
  isFocused: boolean;
  fieldId: string;
  textAlign?: 'left' | 'right' | 'center';
  className?: string;
  rightAddon?: ReactNode;
  onFocus?: () => void;
  onBlur?: () => void;
  children: ReactNode;
};

const baseClassByMode: Record<CellShellMode, string> = {
  view: 'border w-full h-full flex justify-between gap-2 items-center px-3 outline-hidden',
  edit: 'group flex justify-between gap-2 items-center w-full h-full',
  create: 'group flex justify-between gap-2 items-center w-full h-full',
};

const containerClassByVariant: Record<EditCellVariant, string> = {
  default: '',
  highlighted: 'bg-amber-100',
  'ai-edited': '',
  'ai-suggested-added': 'bg-sky-50',
  'ai-suggested-modified': 'bg-sky-50',
  warning: 'text-warning-700 bg-warning-50 cursor-pointer hover:bg-warning-100',
  error: 'text-red-700 bg-red-50 cursor-pointer hover:bg-red-100',
  'pending-validation':
    'bg-blue-50 text-blue-700 focus-within:text-inherit focus-within:bg-transparent! hover:bg-blue-100',
};

const viewModePaddingByVariant: Record<EditCellVariant, string> = {
  default: 'py-3',
  highlighted: 'py-3',
  'ai-edited': 'py-3',
  'ai-suggested-added': 'py-3',
  'ai-suggested-modified': 'py-3',
  warning: 'py-2.5',
  error: 'py-2.5',
  'pending-validation': 'py-3',
};

const focusedBorderClassByVariant: Record<EditCellVariant, string> = {
  default: 'border-transparent',
  highlighted: 'border-transparent',
  'ai-edited': 'border-transparent',
  'ai-suggested-added': 'border-sky-500',
  'ai-suggested-modified': 'border-sky-500',
  warning: 'border-warning-500',
  error: 'border-red-500',
  'pending-validation': 'border-transparent',
};

const focusedWithinEditClassByVariant: Record<EditCellVariant, string> = {
  default: 'hover:bg-procuros-green-50 focus-within:bg-transparent!',
  highlighted: '',
  'ai-edited': '',
  'ai-suggested-added': 'focus-within:bg-transparent!',
  'ai-suggested-modified': 'focus-within:bg-transparent!',
  warning: 'hover:bg-warning-100 focus-within:text-inherit focus-within:bg-transparent!',
  error: 'focus-within:text-inherit focus-within:bg-transparent!',
  'pending-validation': 'focus-within:text-inherit focus-within:bg-transparent!',
};

const trailingIconByVariant: Record<EditCellVariant, ReactNode> = {
  default: null,
  highlighted: null,
  'ai-edited': <SuggestedContentIcon mode="view" />,
  'ai-suggested-added': <SuggestedContentIcon mode="view" />,
  'ai-suggested-modified': <SuggestedContentIcon mode="view" />,
  warning: <ExclamationTriangleIcon className="size-5 shrink-0" />,
  error: <ExclamationCircleIcon className="size-5 shrink-0 text-red-700" />,
  'pending-validation': null,
};

const interactiveVariants: ReadonlySet<EditCellVariant> = new Set<EditCellVariant>([
  'error',
  'warning',
  'ai-suggested-added',
  'ai-suggested-modified',
]);

// View and edit modes have fundamentally different focus models, hence the
// `mode === 'view'` branches throughout. In edit mode the cell renders a real
// `<input>` that owns focus, navigation (`data-field`), tabIndex, and trailing
// icons (rendered inside the input border via `TrailingAddOn`). In view mode
// there is no input, so the wrapper itself takes on those roles plus its own
// visual indicators (palette, underline, focus border). Splitting into
// ViewCellShell + EditCellShell would remove the branches at the cost of
// duplicating already-asymmetric variant→class maps.
export const CellShell = forwardRef<HTMLDivElement, CellShellProps>(function CellShell(
  { variant, mode, isFocused, fieldId, textAlign, className, rightAddon, onFocus, onBlur, children },
  ref,
) {
  const isInteractive = interactiveVariants.has(variant);
  const isRightAligned = textAlign === 'right';
  const trailingIcon = mode === 'edit' ? null : trailingIconByVariant[variant];
  const wrappedRightAddon = rightAddon ? <span className="text-gray-500">{rightAddon}</span> : null;
  const trailingNode = trailingIcon ?? wrappedRightAddon;
  const trailingOnLeading = Boolean(trailingIcon) && isRightAligned;

  const composedClassName = cn(
    baseClassByMode[mode],
    containerClassByVariant[variant],
    mode === 'view' && viewModePaddingByVariant[variant],
    mode === 'view' && variant === 'error' && 'underline',
    mode === 'view' && (isFocused ? focusedBorderClassByVariant[variant] : 'border-transparent'),
    mode !== 'view' && focusedWithinEditClassByVariant[variant],
    className,
  );

  const layout = trailingNode ? (
    <div className="flex w-full items-center gap-1">
      {trailingOnLeading && <span className="shrink-0">{trailingNode}</span>}
      <div className="min-w-0 flex-1">{children}</div>
      {!trailingOnLeading && <span className="shrink-0">{trailingNode}</span>}
    </div>
  ) : (
    children
  );

  return (
    <div
      data-field={mode === 'view' ? fieldId : undefined}
      data-cell-variant={variant}
      tabIndex={mode === 'view' && isInteractive ? 0 : undefined}
      onFocus={onFocus}
      onBlur={onBlur}
      className={composedClassName}
      ref={ref}
    >
      {layout}
    </div>
  );
});
